<template>
  <div class="c-infohub" :class="infoHubClass">
    <div class="c-infohub__header">
      <h2>Information Hub</h2>
      <h3 v-if="subHeader">{{ subHeader }}</h3>
      <a v-if="showAllAdvisoriesLink" href="/advisory" class="c-cta--tertiary">
        See All Advisories
      </a>
    </div>
    <div v-if="pages" class="c-infohub__inner">
      <div v-for="page in pages" :key="page.id" class="c-infohub-link">
        <div class="c-infohub-link__header">
          <span class="c-infohub-link__type">{{ page.templateName }}</span>
          <span class="c-infohub-link__date">{{ page.publishedAt }}</span>
        </div>
        <div class="c-infohub-link__inner">
          <a :href="page.url">{{ page.displayName }}</a>
        </div>
        <div class="c-infohub-link__footer">
          <a :href="page.url"
            >Read More <span class="sr-only">{{ page.templateName }}</span></a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { useQuery } from '@vue/apollo-composable';
import { InfoHubQuery } from './CIS-InfoHub.graphql';

export default {
  name: 'CIS-InfoHub',
  setup() {
    const instance = getCurrentInstance();

    const variables = () => {
      return {
        contextItem:
          instance?.appContext?.config?.globalProperties?.$jss?.sitecoreContext()?.itemId ??
          '{00000000-0000-0000-0000-000000000000}' // default value to prevent GraphQL errors
      };
    };

    const { result, loading, error } = useQuery(InfoHubQuery, variables());

    return {
      result,
      loading,
      error
    };
  },
  methods: {
    convertToDate(timestamp) {
      const date = new Date(timestamp);
      // use 2-digits for month and day, and use UTC so it isn't adjusted to local timezone, which can change the shown date
      const dateOptions = {
        weekday: undefined,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'UTC'
      };
      // Use replace function for separator to be a dot instead of slash or dash
      return date?.toLocaleDateString(undefined, dateOptions)?.replace(/[^0-9]/g, '.');
    },
    isHome() {
      // Note: this will match any page named "home"; ex. /sitecore/content/cisecurity/ngen/home/cis/common/infohub/home
      return !!(this.$jss?.sitecoreContext()?.routeName?.toLowerCase() === 'home');
    },
    isAdvisory() {
      return !!(this?.result?.context?.template?.name === 'CIS-Resource-Advisory-RouteType');
    },
    isBenchmark() {
      return !!(this?.result?.context?.template?.name === 'CIS-Resource-Benchmark-RouteType');
    }
  },
  computed: {
    infoHub() {
      const children = this?.result?.infoHubs?.children;

      const findInfoHub = (ownerId) => {
        return children?.find((i) => i?.owner?.value === ownerId);
      };

      const findGeneric = () => {
        return children?.find((i) => i?.isGeneric?.boolValue);
      };

      if (this.isHome()) {
        return children?.find((i) => i?.isHomepage?.boolValue);
      }

      const firstOwner = this?.result?.context?.owners?.value?.split('|')[0];

      return findInfoHub(firstOwner) ?? findGeneric();
    },
    pages() {
      return (
        this.infoHub?.pages?.targetItems?.map((i) => {
          const publishedAt = i.publishedAt?.dateValue
            ? this.convertToDate(i.publishedAt?.dateValue)
            : '';
          const templateName =
            this?.result?.templates?.children?.find((t) => t.name === i.template?.name)
              ?.displayName ?? 'Page';

          return {
            id: i.id,
            name: i.name,
            url: i.url,
            displayName: i?.title?.value ?? i.displayName,
            templateName: templateName,
            publishedAt: publishedAt
          };
        }) ?? []
      );
    },
    infoHubClass() {
      return { home: this.isHome() };
    },
    subHeader() {
      if (this.isAdvisory()) {
        return 'Advisories';
      }

      if (this.isBenchmark()) {
        return (
          this?.result?.context?.pageTitle?.value ??
          this?.result?.context?.displayName ??
          this?.result?.context?.name
        );
      }

      return this.infoHub?.subTitle?.value ?? false;
    },
    showAllAdvisoriesLink() {
      return this.isAdvisory();
    }
  }
};
</script>

<style lang="scss">
.c-infohub {
  clear: both;
  background-color: $box-gray;
  @include padding-y(2rem);

  @include desktop-up {
    @include padding-y(3rem, 2rem);
  }

  &__header {
    @include padding-x-thin;
    margin: 0 auto;

    h2 {
      font-size: 2.4rem;
      line-height: 3rem;
      font-weight: 200;
      margin: 0 0 1.5rem 0;

      @include desktop-up {
        display: inline-block;
        font-size: 3.6rem;
        line-height: 4.3rem;
        margin: 0;
        min-height: 5.4rem;
      }
    }

    h3 {
      @include font-styles('Arial', 2rem, 200, normal, normal, 0, $dark-blue);
      text-transform: unset;
      border-left: 2px solid $orange;
      vertical-align: middle;
      display: inline-flex;
      padding: 0.5rem 0 0.5rem 1.5rem;

      @include desktop-up {
        display: inline;
        font-size: 2.8rem;
        line-height: 3.5rem;
        margin-left: 1rem;
        padding: 0 0 0 1.5rem;
      }

      &::before {
        display: initial;
      }
    }

    div {
      display: inline-block;
    }
  }

  &__inner {
    @include padding-x-thin;
    margin: 0 auto;

    @include desktop-up {
      display: flex;
      clear: both;
    }
  }

  .c-infohub-link {
    padding: 3rem 0 1.5rem 0;
    border-bottom: solid 1px $divider;

    @include desktop-up {
      display: flex;
      flex: 1 1 0;
      flex-flow: column nowrap;
      padding: 3.5rem 2rem 2rem 2.2rem;
      margin-right: 2rem;
      border: none;
    }

    &:first-child {
      padding: 1.5rem 0 1.5rem 0;

      @include desktop-up {
        padding: 3.5rem 2rem 2rem 0.2rem;
      }
    }

    &:last-child {
      border: none;
    }

    &__header {
      @include font-styles('Arial Narrow', 1.4rem, 600, normal, normal, 0, $dark-blue);
      text-transform: uppercase;

      .c-infohub-link__date {
        padding-left: 1rem;
      }
    }

    &__inner {
      margin: 1rem 0 1.7rem;

      a {
        @include font-styles('Arial Narrow', 2rem, 500, normal, normal, 0, $blue);

        &:hover {
          text-decoration: underline;
        }
      }
    }

    &__footer {
      margin-top: auto;

      a {
        display: inline-flex;
        padding: 1.1rem 4rem 1.1rem 0;
        text-transform: uppercase;
        justify-content: center;
        align-items: center;
        margin-bottom: 0;
        @include font-styles('Arial Narrow', 1.8rem, 700, normal, normal, 0, $blue);

        &:after {
          content: '';
          position: relative;
          background-image: url(~@/assets/images/icon-arrow-right-blue-sm.svg);
          background-repeat: no-repeat;
          background-size: contain;
          margin-left: 1rem;
          width: 1.9rem;
          min-width: 1.9rem;
          height: 1.5rem;
          min-height: 1.5rem;
          transition: 0.25s;
          left: 0;
        }

        &:hover,
        &:focus,
        &:active {
          color: $blue;
          text-decoration: none;
        }

        &:hover {
          &:after {
            left: 1rem;
          }
        }
      }
    }
  }

  .c-cta--tertiary {
    float: right;
    padding-top: 0.6rem;
    padding-right: 0;

    @include desktop-up {
      padding-top: 1.3rem;
      padding-right: 2rem;
    }

    &:hover {
      &:after {
        left: 1rem;
      }
    }
  }
}
</style>
